import {
    type ReactNode,
    useState
} from "react";
import {
    useWindowDimensions,
    StyleSheet,
    ScrollView,
    View
} from "react-native";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    RadioButton,
    InputGroup,
    RadioGroup,
    TextInput,
    SelectBox,
    Separator,
    CheckBox,
    Stepper,
    Button,
    Badge,
    Card,
    Item,
    Icon,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../i18n";
import {
    ChevronLeft,
    ShieldCheck,
    CreditCard,
    ArrowRight,
    Check,
    Lock
} from "lucide-react-native";
import {
    Ellipse,
    Circle,
    Path,
    Rect,
    Svg,
    G
} from "react-native-svg";
import {
    createActionIcon,
    createKitIcon
} from "../components/kitIcons";

const CardIcon = createKitIcon(CreditCard);

const LockIcon = createKitIcon(Lock);

const BackIcon = createActionIcon(ChevronLeft);

const NextIcon = createActionIcon(ArrowRight);

const STEPS = [
    "cart",
    "delivery",
    "payment",
    "confirm"
];

const CITIES = [
    {
        districts: [
            "kadikoy",
            "besiktas",
            "uskudar"
        ],
        id: "istanbul"
    },
    {
        districts: [
            "cankaya",
            "kecioren",
            "yenimahalle"
        ],
        id: "ankara"
    },
    {
        districts: [
            "karsiyaka",
            "bornova",
            "konak"
        ],
        id: "izmir"
    }
];

const ITEMS = [
    {
        id: "ethiopia",
        kind: "bag",
        price: 570,
        color: "1",
        qty: 2
    },
    {
        id: "colombia",
        kind: "bag",
        price: 260,
        color: "2",
        qty: 1
    },
    {
        kind: "dripper",
        id: "dripper",
        price: 495,
        color: "3",
        qty: 1
    }
];

const SHIPPING = [
    {
        id: "standard",
        price: 0
    },
    {
        id: "express",
        price: 49
    }
];

const COUPON_CODE = "HOSGELDIN10";

const COUPON_RATE = 0.1;

const MARK_BAG = "M8 11h12l-1.4 8.2a2 2 0 0 1-2 1.8h-5.2a2 2 0 0 1-2-1.8z";

const MARK_HANDLE = "M10.5 11a3.5 3.5 0 0 1 7 0";

const BAG_BODY = "M38 36h84l9 126a7 7 0 0 1-7 7H36a7 7 0 0 1-7-7z";

const BAG_SHOULDER = "M38 36h84l1.4 20H36.6z";

const BAG_CAP = "M44 22c0-4 4-6 8-6h56c4 0 8 2 8 6v14H44z";

const BAG_LEAF = "M74 106c2-8 8-12 14-12-1 7-5 12-14 12z";

const DRIPPER_CONE = "M26 18h48l-14 24H40z";

const DRIPPER_BODY = "M32 52h36l-3 30a5 5 0 0 1-5 4H40a5 5 0 0 1-5-4z";

const DRIPPER_FILL = "M34 66h32l-1.5 16a5 5 0 0 1-5 4H40a5 5 0 0 1-5-4z";

const stylesheet = StyleSheet.create({
    spread: {
        justifyContent: "space-between",
        alignItems: "center",
        flexDirection: "row"
    },
    baseline: {
        justifyContent: "space-between",
        alignItems: "baseline",
        flexDirection: "row"
    },
    header: {
        justifyContent: "space-between",
        alignItems: "center",
        flexDirection: "row"
    },
    thumb: {
        justifyContent: "center",
        alignItems: "center",
        overflow: "hidden"
    },
    columns: {
        alignItems: "flex-start"
    },
    row: {
        alignItems: "center",
        flexDirection: "row"
    },
    fields: {
        flexDirection: "row",
        flexWrap: "wrap"
    },
    wrapper: {
        alignSelf: "center",
        width: "100%"
    },
    figure: {
        height: "100%",
        width: "100%"
    },
    grow: {
        minWidth: 0,
        flex: 1,
        flexShrink: 1
    },
    full: {
        width: "100%"
    },
    option: {
        width: "100%"
    },
    code: {
        flex: 1
    }
});

type ArtProps = {
    paper: string;
    fill: string;
    kind: string;
    ink: string;
};

const Art = ({
    paper,
    fill,
    kind,
    ink
}: ArtProps) => <Svg
    viewBox={kind === "bag" ? "0 0 160 180" : "0 0 100 100"}
    preserveAspectRatio="xMidYMid meet"
    style={stylesheet.figure}
>
    {
        kind === "bag" ?
            <G>
                <Ellipse
                    opacity={0.14}
                    fill={ink}
                    cy={170}
                    cx={80}
                    rx={50}
                    ry={5}
                />
                <Path
                    d={BAG_BODY}
                    fill={fill}
                />
                <Path
                    d={BAG_SHOULDER}
                    opacity={0.16}
                    fill={ink}
                />
                <Path
                    fill={fill}
                    d={BAG_CAP}
                />
                <Rect
                    fill={paper}
                    height={70}
                    width={68}
                    rx={6}
                    x={46}
                    y={78}
                />
                <Circle
                    strokeWidth={5}
                    stroke={fill}
                    fill="none"
                    cy={102}
                    cx={80}
                    r={12}
                />
                <Path
                    d={BAG_LEAF}
                    fill={fill}
                />
            </G>
        :
            <G>
                <Path
                    d={DRIPPER_CONE}
                    fill={paper}
                />
                <Rect
                    fill={fill}
                    height={6}
                    width={32}
                    rx={2}
                    x={34}
                    y={42}
                />
                <Path
                    d={DRIPPER_BODY}
                    opacity={0.85}
                    fill={fill}
                />
                <Path
                    d={DRIPPER_FILL}
                    fill={fill}
                />
            </G>
    }
</Svg>;

const getOptionTitle = (option: {
    name: string;
}) => option.name;

const getOptionKey = (option: {
    id: string;
}) => option.id;

const Checkout01 = () => {
    const {
        t
    } = useDocsI18n();

    const {
        activeLocale
    } = NCoreUIKitLocalize.useContext();

    const {
        controlHeights,
        shapeConfigs,
        iconSizes,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        width
    } = useWindowDimensions();

    const [
        city,
        setCity
    ] = useState("istanbul");

    const [
        district,
        setDistrict
    ] = useState("kadikoy");

    const [
        isSameAddress,
        setIsSameAddress
    ] = useState(true);

    const [
        shipping,
        setShipping
    ] = useState("standard");

    const [
        code,
        setCode
    ] = useState(COUPON_CODE);

    const [
        appliedCode,
        setAppliedCode
    ] = useState<string | undefined>(COUPON_CODE);

    const isCompact = width < shapeConfigs.compactBreakpoint;

    const isStacked = width < shapeConfigs.wideBreakpoint;

    const categorical = colors.chart.categorical;

    const categoricalArea = colors.chart.categoricalArea;

    const ink = colors.content.container.inverse;

    const paper = colors.content.container.default;

    const money = new Intl.NumberFormat(activeLocale, {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
        style: "currency",
        currency: "TRY"
    });

    const subtotal = ITEMS.reduce((sum, item) => sum + item.price, 0);

    const isCouponValid = appliedCode === COUPON_CODE;

    const discount = isCouponValid ? subtotal * COUPON_RATE : 0;

    const shippingPrice = SHIPPING.find((item) => item.id === shipping)?.price ?? 0;

    const total = subtotal - discount + shippingPrice;

    const cityData = CITIES.find((item) => item.id === city) ?? CITIES[0];

    const cityOptions = CITIES.map((item) => ({
        name: t(`blocks.checkout01.city.${item.id}`),
        id: item.id
    }));

    const districtOptions = (cityData?.districts ?? []).map((id) => ({
        name: t(`blocks.checkout01.district.${id}`),
        id
    }));

    const handlePress = () => undefined;

    const getColor = (key: string) => categorical[key as keyof typeof categorical];

    const getArea = (key: string) => categoricalArea[key as keyof typeof categoricalArea];

    const handleCityChange = (items: Array<{
        __key: string;
    }>) => {
        const next = items[0]?.__key ?? "istanbul";

        setCity(next);

        setDistrict(CITIES.find((item) => item.id === next)?.districts[0] ?? "");
    };

    const handleDistrictChange = (items: Array<{
        __key: string;
    }>) => setDistrict(items[0]?.__key ?? district);

    const handleSameToggle = () => setIsSameAddress((current) => !current);

    const handleApply = () => setAppliedCode(code.trim().toUpperCase());

    const renderStep = (key: string) => ({
        title: t(`blocks.checkout01.step.${key}`),
        key
    });

    const renderShipping = (option: typeof SHIPPING[number]) => <RadioButton
        title={t(`blocks.checkout01.shipping.${option.id}`, [
            option.price === 0 ? t("blocks.checkout01.free") : money.format(option.price)
        ])}
        subTitle={t(`blocks.checkout01.shipping.${option.id}.desc`)}
        style={stylesheet.option}
        value={option.id}
        key={option.id}
        variant="card"
    />;

    const renderItem = (item: typeof ITEMS[number]) => <Item
        variant="plain"
        key={item.id}
    >
        <Item.Media
            variant="plain"
        >
            <Badge.Indicator
                count={item.qty}
                type="primary"
            >
                <View
                    style={[
                        stylesheet.thumb,
                        {
                            height: controlHeights.large + spaces.spacingLg,
                            width: controlHeights.large + spaces.spacingLg,
                            backgroundColor: getArea(item.color),
                            borderRadius: spaces.spacingSm,
                            padding: spaces.spacingSm
                        }
                    ]}
                >
                    <Art
                        fill={getColor(item.color)}
                        kind={item.kind}
                        paper={paper}
                        ink={ink}
                    />
                </View>
            </Badge.Indicator>
        </Item.Media>
        <Item.Content>
            <Item.Title>
                {t(`blocks.checkout01.item.${item.id}`)}
            </Item.Title>
            <Item.Description>
                {t(`blocks.checkout01.item.${item.id}.variant`)}
            </Item.Description>
        </Item.Content>
        <Item.Actions>
            <Text
                variant="labelMediumSize"
                color="high"
            >
                {money.format(item.price)}
            </Text>
        </Item.Actions>
    </Item>;

    const renderTotalRow = (label: string, value: string, color: "success" | "high" | "mid" = "high") => <View
        style={[
            stylesheet.spread,
            {
                gap: spaces.spacingSm
            }
        ]}
    >
        <Text
            variant="bodyMediumSize"
            color="mid"
        >
            {label}
        </Text>
        <Text
            variant="bodyMediumSize"
            color={color}
        >
            {value}
        </Text>
    </View>;

    const renderFieldPair = (first: ReactNode, second: ReactNode) => <View
        style={[
            stylesheet.fields,
            {
                columnGap: spaces.spacingMd,
                rowGap: spaces.spacingMd
            }
        ]}
    >
        <View
            style={[
                stylesheet.grow,
                {
                    minWidth: isCompact ? "100%" : shapeConfigs.fieldMinWidth
                }
            ]}
        >
            {first}
        </View>
        <View
            style={[
                stylesheet.grow,
                {
                    minWidth: isCompact ? "100%" : shapeConfigs.fieldMinWidth
                }
            ]}
        >
            {second}
        </View>
    </View>;

    return <ScrollView>
        <View>
            <View
                style={[
                    stylesheet.wrapper,
                    stylesheet.header,
                    {
                        maxWidth: shapeConfigs.wideBreakpoint + shapeConfigs.sideListWidth,
                        paddingHorizontal: isCompact ? spaces.spacingMd : spaces.spacingLg,
                        paddingVertical: spaces.spacingSm
                    }
                ]}
            >
                <View
                    style={[
                        stylesheet.row,
                        {
                            gap: spaces.spacingXs
                        }
                    ]}
                >
                    <Svg
                        height={iconSizes.large * 1.5}
                        width={iconSizes.large * 1.5}
                        viewBox="0 0 28 28"
                    >
                        <Circle
                            fill={ink}
                            cx={14}
                            cy={14}
                            r={14}
                        />
                        <Path
                            d={MARK_BAG}
                            fill={paper}
                        />
                        <Path
                            strokeLinecap="round"
                            strokeWidth={1.6}
                            d={MARK_HANDLE}
                            stroke={paper}
                            fill="none"
                        />
                    </Svg>
                    <Text
                        variant="titleLargeSize"
                        color="high"
                    >
                        {t("blocks.checkout01.brand")}
                    </Text>
                </View>
                <View
                    style={[
                        stylesheet.row,
                        {
                            gap: spaces.spacingXs
                        }
                    ]}
                >
                    <Icon
                        icon={Lock}
                        color="mid"
                    />
                    <Text
                        variant="bodyMediumSize"
                        color="mid"
                    >
                        {t("blocks.checkout01.secure")}
                    </Text>
                </View>
            </View>
            <Separator
                length="100%"
            />
        </View>
        <View
            style={[
                stylesheet.wrapper,
                {
                    maxWidth: shapeConfigs.wideBreakpoint + shapeConfigs.sideListWidth,
                    paddingHorizontal: isCompact ? spaces.spacingMd : spaces.spacingLg,
                    paddingVertical: spaces.spacingLg,
                    gap: spaces.spacingXl
                }
            ]}
        >
            <View
                style={[
                    stylesheet.columns,
                    {
                        flexDirection: isStacked ? "column" : "row",
                        gap: spaces.spacingXl
                    }
                ]}
            >
                <View
                    style={[
                        stylesheet.grow,
                        stylesheet.full,
                        {
                            gap: spaces.spacingMd
                        }
                    ]}
                >
                    <Stepper
                        accessibilityLabel={t("blocks.checkout01.steps")}
                        steps={STEPS.map(renderStep)}
                        activeIndex={1}
                    />
                    <Card
                        variant="outline"
                    >
                        <Card.Header>
                            <Card.Title>
                                {t("blocks.checkout01.address.title")}
                            </Card.Title>
                            <Card.Description>
                                {t("blocks.checkout01.address.lead")}
                            </Card.Description>
                        </Card.Header>
                        <Card.Content>
                            <View
                                style={{
                                    gap: spaces.spacingMd
                                }}
                            >
                                {
                                    renderFieldPair(<TextInput
                                        defaultValue={t("blocks.checkout01.address.nameValue")}
                                        title={t("blocks.checkout01.address.name")}
                                        spreadBehaviour="stretch"
                                        autoComplete="name"
                                    />, <InputGroup
                                        hintText={t("blocks.checkout01.address.phoneHint")}
                                        title={t("blocks.checkout01.address.phone")}
                                        spreadBehaviour="stretch"
                                    >
                                        <InputGroup.Addon
                                            variant="attached"
                                        >
                                            <InputGroup.Text>
                                                +90
                                            </InputGroup.Text>
                                        </InputGroup.Addon>
                                        <InputGroup.Input
                                            defaultValue={t("blocks.checkout01.address.phoneValue")}
                                            keyboardType="phone-pad"
                                            autoComplete="tel"
                                        />
                                    </InputGroup>)
                                }
                                <InputGroup
                                    title={t("blocks.checkout01.address.street")}
                                    spreadBehaviour="stretch"
                                >
                                    <InputGroup.TextArea
                                        defaultValue={t("blocks.checkout01.address.streetValue")}
                                    />
                                </InputGroup>
                                <View
                                    style={[
                                        stylesheet.fields,
                                        {
                                            columnGap: spaces.spacingMd,
                                            rowGap: spaces.spacingMd
                                        }
                                    ]}
                                >
                                    <View
                                        style={[
                                            stylesheet.grow,
                                            {
                                                minWidth: isCompact ? "100%" : shapeConfigs.fieldMinWidth * 0.75
                                            }
                                        ]}
                                    >
                                        <SelectBox
                                            title={t("blocks.checkout01.address.city")}
                                            titleExtractor={getOptionTitle}
                                            onChange={handleCityChange}
                                            keyExtractor={getOptionKey}
                                            spreadBehaviour="stretch"
                                            defaultValue="istanbul"
                                            data={cityOptions}
                                        />
                                    </View>
                                    <View
                                        style={[
                                            stylesheet.grow,
                                            {
                                                minWidth: isCompact ? "100%" : shapeConfigs.fieldMinWidth * 0.75
                                            }
                                        ]}
                                    >
                                        <SelectBox
                                            title={t("blocks.checkout01.address.district")}
                                            onChange={handleDistrictChange}
                                            titleExtractor={getOptionTitle}
                                            keyExtractor={getOptionKey}
                                            spreadBehaviour="stretch"
                                            defaultValue={district}
                                            data={districtOptions}
                                            key={city}
                                        />
                                    </View>
                                    <View
                                        style={[
                                            stylesheet.grow,
                                            {
                                                minWidth: isCompact ? "100%" : shapeConfigs.fieldMinWidth * 0.75
                                            }
                                        ]}
                                    >
                                        <TextInput
                                            title={t("blocks.checkout01.address.postal")}
                                            keyboardType="number-pad"
                                            spreadBehaviour="stretch"
                                            defaultValue="34710"
                                            isOptional={true}
                                        />
                                    </View>
                                </View>
                                <CheckBox
                                    title={t("blocks.checkout01.address.same")}
                                    onPress={handleSameToggle}
                                    isChecked={isSameAddress}
                                />
                            </View>
                        </Card.Content>
                    </Card>
                    <Card
                        variant="outline"
                    >
                        <Card.Header>
                            <Card.Title>
                                {t("blocks.checkout01.shipping.title")}
                            </Card.Title>
                        </Card.Header>
                        <Card.Content>
                            <RadioGroup
                                aria-label={t("blocks.checkout01.shipping.title")}
                                onValueChange={setShipping}
                                value={shipping}
                            >
                                {SHIPPING.map(renderShipping)}
                            </RadioGroup>
                        </Card.Content>
                    </Card>
                    <Card
                        variant="outline"
                    >
                        <Card.Header>
                            <Card.Title>
                                {t("blocks.checkout01.payment.title")}
                            </Card.Title>
                            <Card.Description>
                                {t("blocks.checkout01.payment.lead")}
                            </Card.Description>
                            <Card.Action>
                                <Badge
                                    title={t("blocks.checkout01.payment.next")}
                                    variant="tonal"
                                    type="neutral"
                                />
                            </Card.Action>
                        </Card.Header>
                        <Card.Content>
                            <View
                                style={{
                                    gap: spaces.spacingMd
                                }}
                            >
                                <TextInput
                                    placeholder={t("blocks.checkout01.payment.numberPlaceholder")}
                                    title={t("blocks.checkout01.payment.number")}
                                    spreadBehaviour="stretch"
                                    rightIcon={CardIcon}
                                    isDisabled={true}
                                />
                                {
                                    renderFieldPair(<TextInput
                                        placeholder={t("blocks.checkout01.payment.expiryPlaceholder")}
                                        title={t("blocks.checkout01.payment.expiry")}
                                        spreadBehaviour="stretch"
                                        isDisabled={true}
                                    />, <TextInput
                                        placeholder={t("blocks.checkout01.payment.cvcPlaceholder")}
                                        title={t("blocks.checkout01.payment.cvc")}
                                        spreadBehaviour="stretch"
                                        rightIcon={LockIcon}
                                        isDisabled={true}
                                    />)
                                }
                            </View>
                        </Card.Content>
                    </Card>
                    <View
                        style={[
                            stylesheet.spread,
                            {
                                gap: spaces.spacingSm,
                                flexWrap: "wrap"
                            }
                        ]}
                    >
                        <Button
                            title={t("blocks.checkout01.back")}
                            onPress={handlePress}
                            variant="ghost"
                            icon={BackIcon}
                            type="neutral"
                        />
                        <Text
                            variant="bodySmallSize"
                            color="mid"
                        >
                            {t("blocks.checkout01.notConfirmed")}
                        </Text>
                    </View>
                </View>
                <Card
                    style={{
                        width: isStacked ? "100%" : shapeConfigs.cardWidth + spaces.spacingXxl
                    }}
                    variant="outline"
                >
                    <Card.Header>
                        <Card.Title>
                            {t("blocks.checkout01.summary.title")}
                        </Card.Title>
                        <Card.Action>
                            <Button
                                title={t("blocks.checkout01.summary.edit")}
                                onPress={handlePress}
                                variant="link"
                                size="small"
                            />
                        </Card.Action>
                    </Card.Header>
                    <Card.Content>
                        <View
                            style={{
                                gap: spaces.spacingLg
                            }}
                        >
                            <View>
                                {ITEMS.map(renderItem)}
                            </View>
                            <View
                                style={{
                                    gap: spaces.spacingXs
                                }}
                            >
                                <InputGroup
                                    title={t("blocks.checkout01.coupon.title")}
                                    spreadBehaviour="stretch"
                                >
                                    <InputGroup.Input
                                        autoCapitalize="characters"
                                        onChangeText={setCode}
                                        value={code}
                                    />
                                    <InputGroup.Addon
                                        align="end"
                                    >
                                        <InputGroup.Button
                                            title={t("blocks.checkout01.coupon.apply")}
                                            onPress={handleApply}
                                        />
                                    </InputGroup.Addon>
                                </InputGroup>
                                {
                                    appliedCode === undefined ?
                                        null
                                    :
                                        <View
                                            style={[
                                                stylesheet.row,
                                                {
                                                    gap: spaces.spacingXs
                                                }
                                            ]}
                                        >
                                            <Icon
                                                color={isCouponValid ? "success" : "danger"}
                                                size="small"
                                                icon={Check}
                                            />
                                            <Text
                                                color={isCouponValid ? "success" : "danger"}
                                                variant="bodySmallSize"
                                            >
                                                {t(isCouponValid ? "blocks.checkout01.coupon.applied" : "blocks.checkout01.coupon.invalid")}
                                            </Text>
                                        </View>
                                }
                            </View>
                            <Separator
                                length="100%"
                            />
                            <View
                                style={{
                                    gap: spaces.spacingSm
                                }}
                            >
                                {renderTotalRow(t("blocks.checkout01.summary.subtotal"), money.format(subtotal))}
                                {
                                    isCouponValid ?
                                        renderTotalRow(t("blocks.checkout01.summary.discount", [
                                            COUPON_CODE
                                        ]), `−${money.format(discount)}`, "success")
                                    :
                                        null
                                }
                                {renderTotalRow(t("blocks.checkout01.summary.shipping"), shippingPrice === 0 ? t("blocks.checkout01.free") : money.format(shippingPrice))}
                            </View>
                            <Separator
                                length="100%"
                            />
                            <View
                                style={[
                                    stylesheet.baseline,
                                    {
                                        gap: spaces.spacingSm
                                    }
                                ]}
                            >
                                <Text
                                    variant="titleMediumSize"
                                    color="high"
                                >
                                    {t("blocks.checkout01.summary.total")}
                                </Text>
                                <View
                                    style={[
                                        stylesheet.row,
                                        {
                                            gap: spaces.spacingXs
                                        }
                                    ]}
                                >
                                    <Text
                                        variant="bodySmallSize"
                                        color="mid"
                                    >
                                        {t("blocks.checkout01.summary.vat")}
                                    </Text>
                                    <Text
                                        variant="headlineSmallSize"
                                        color="high"
                                    >
                                        {money.format(total)}
                                    </Text>
                                </View>
                            </View>
                            <View
                                style={{
                                    gap: spaces.spacingSm
                                }}
                            >
                                <Button
                                    title={t("blocks.checkout01.summary.pay")}
                                    spreadBehaviour="stretch"
                                    onPress={handlePress}
                                    iconDirection="end"
                                    variant="filled"
                                    icon={NextIcon}
                                    type="primary"
                                    size="large"
                                />
                                <View
                                    style={[
                                        stylesheet.row,
                                        {
                                            justifyContent: "center",
                                            gap: spaces.spacingXs
                                        }
                                    ]}
                                >
                                    <Icon
                                        icon={ShieldCheck}
                                        size="small"
                                        color="mid"
                                    />
                                    <Text
                                        variant="bodySmallSize"
                                        color="mid"
                                    >
                                        {t("blocks.checkout01.summary.secure")}
                                    </Text>
                                </View>
                            </View>
                        </View>
                    </Card.Content>
                </Card>
            </View>
        </View>
    </ScrollView>;
};
export default Checkout01;
